'use client';

import Link from '@eliadmin/core/src/components/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { UseFormReturn } from 'react-hook-form';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Paragraph } from '../ui/paragraph';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '../ui/heading';
import { RegisterSuccess } from './register-success';
import { Field } from '../form/labeled-field';

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

export const registerSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

      address: __eliadmin_zod_1().optional(),
      street_number: __eliadmin_zod_1().optional(),
      postcode: __eliadmin_zod_1().optional(),
      city: __eliadmin_zod_1().optional(),
      country: __eliadmin_zod_0({
          id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
        })
        .optional(),

      company_buy: __eliadmin_zod_3().optional(),
      company: __eliadmin_zod_1().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),
      email_company: __eliadmin_zod_1().optional(),
      address_company: __eliadmin_zod_1().optional(),
      street_number_company: __eliadmin_zod_1().optional(),
      postcode_company: __eliadmin_zod_1().optional(),
      city_company: __eliadmin_zod_1().optional(),
      country_company: __eliadmin_zod_0({
          id: __eliadmin_zod_1(),
        })
        .optional(),

      gdpr: __eliadmin_zod_3().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: __eliadmin_zod_3().optional(),

      newsletter_return_url: __eliadmin_zod_1(),
      tos: __eliadmin_zod_3().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': __eliadmin_zod_1().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

// ---------------------------------------------------------------------------
// Strips empty-string values so the API receives null/undefined instead of ""
// which many backends store as null anyway but then return as null, causing
// the profile form to initialise with nulls on first open.
// ---------------------------------------------------------------------------
function omitEmptyStrings<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === '') return [key, undefined];
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return [key, omitEmptyStrings(value as Record<string, unknown>)];
      }
      return [key, value];
    }),
  ) as T;
}

export function RegisterForm({
  initialSocialAuthProviders,
  onShowSignIn,
  inDialog = false,
}: {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  onShowSignIn?: () => void;
  inDialog?: boolean;
}) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    // FIX: prevent RHF from unregistering fields when PersonFields/CompanyFields
    // swap, which would silently drop their values from the submitted payload.
    shouldUnregister: false,
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,

      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: { id: 'SK' },

      company_buy: false,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      email_company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: undefined,

      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const router = useRouter();

  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = form.handleSubmit(async data => {
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      // FIX: strip empty strings before sending so the backend stores null/
      // undefined rather than empty string, which it would then return as null
      // and cause the profile form to show blank fields on first open.
      const payload = omitEmptyStrings({
        ...data,

        locale,
      });

      const resp = await register(payload);

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        // FIX: the register endpoint may return a minimal customer object that
        // lacks address fields. Merge the submitted form data into the customer
        // so the profile form initialises with what the user just typed rather
        // than waiting for the (incomplete) API response.
        customer: {
          ...payload,
          ...resp.data.customer,
          // Ensure country is the full object shape the profile form expects.
          // If the API returned it, use that; otherwise fall back to what was submitted.
          country: resp.data.customer?.country ?? payload.country,
        },
      });

      setIsSuccess(true);
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
      }
    }
  });

  if (isSuccess) {
    return <RegisterSuccess inDialog={inDialog} />;
  }

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8',
          'ea-register-form',
          !inDialog && 'pb-12 pt-32 sm:pt-[180px] lg:pt-[200px]',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <Heading tag="h3" title={t('text261')} className={{ title: 'uppercase' }} />
          <Paragraph className="text-center">
            {t('text623')}&nbsp;
            <span
              className={cn(
                'cursor-pointer font-semibold text-black transition-all hover:opacity-80',
                'custom-underline',
              )}
              onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
            >
              {t('text624')}
            </span>
          </Paragraph>
        </div>

        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-register-form__content',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <FormTitle name={t('text385')} />
            <Field
              type="email"
              name="email"
              label={'E-mail *'}
              placeholder={'E-mail *'}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
            />
            <div className="grid w-full grid-cols-1 gap-4">
              <Field
                type="password"
                name="password"
                label={`${t('text191')} *`}
                placeholder={`${t('text191')} *`}
                control={form.control}
                className={{ wrapper: 'col-span-1 w-full' }}
              />
              <Field
                type="password"
                name="password_confirmation"
                label={`${t('text3183')} *`}
                placeholder={`${t('text3183')} *`}
                control={form.control}
                className={{ wrapper: 'col-span-1 w-full' }}
              />
            </div>
          </div>

          <div className="grid w-full grid-cols-12 gap-4">
            <FormTitle name={t('text2350')} className="col-span-12" />
            <FormField
              control={form.control}
              name="company_buy"
              render={({ field }) => (
                <FormItem
                  className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
                >
                  <FormControl>
                    <RadioGroup
                      onValueChange={val => field.onChange(val === 'true')}
                      value={String(field.value)}
                      className="flex gap-6"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="false" className="h-8 w-8" />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-semibold uppercase">
                          {t('text2234')}
                        </FormLabel>
                      </FormItem>

                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="true" className="h-8 w-8" />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-semibold uppercase">
                          {t('text1432')}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('company_buy') === true ? (
              <CompanyFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            ) : (
              <PersonFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            )}
          </div>

          <div className="col-span-12 flex w-full flex-col items-start justify-start gap-4">
            <FormTitle name={t('text803')} />
            {form.watch('company_buy') === true && (
              <Field
                name="email_company"
                type="email"
                label={'Email *'}
                placeholder={'Email *'}
                control={form.control}
                className={{ wrapper: 'w-full' }}
              />
            )}
            <Field
              name="phone"
              label={`${t('text1258')} *`}
              placeholder={`${t('text1258')} *`}
              control={form.control}
              className={{ wrapper: 'w-full' }}
            />
          </div>
        </div>

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-8',
            'ea-register-form__footer',
          )}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-medium">
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="font-bold text-primary underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('hidden w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('w-full text-white', 'ea-register-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text758')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ name, className }: { name: string; className?: string }) {
  return <Heading className={{ container: className }} title={name} tag="h6" />;
}

function PersonFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        name="name"
        label={`${t('text33')} *`}
        placeholder={`${t('text33')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="surname"
        label={`${t('text34')} *`}
        placeholder={`${t('text34')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="address"
        label={`${t('text157')} *`}
        placeholder={`${t('text157')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        name="street_number"
        label={`${t('text438')} *`}
        placeholder={`${t('text438')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode"
        label={`${t('text40')} *`}
        placeholder={`${t('text40')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city"
        label={`${t('text39')} *`}
        placeholder={`${t('text39')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        name="country.id"
        type="select"
        label={`${t('text519')} *`}
        placeholder={`${t('text519')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}

function CompanyFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        name="ico"
        label={`${t('text42')} *`}
        placeholder={`${t('text42')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="dic"
        label={`${t('text43')} *`}
        placeholder={`${t('text43')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="ic_dph"
        label={`${t('text44')} *`}
        placeholder={`${t('text44')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        name="company"
        label={`${t('text1188')} *`}
        placeholder={`${t('text1188')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        name="name"
        label={`${t('text33')} *`}
        placeholder={`${t('text33')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="surname"
        label={`${t('text34')} *`}
        placeholder={`${t('text34')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="address_company"
        label={`${t('text38')} *`}
        placeholder={`${t('text38')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        name="street_number_company"
        label={`${t('text438')} *`}
        placeholder={`${t('text438')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode_company"
        label={`${t('text40')} *`}
        placeholder={`${t('text40')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city_company"
        label={`${t('text39')} *`}
        placeholder={`${t('text39')} *`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        control={form.control}
        type="select"
        name="country_company.id"
        label={t('text519')}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}
